import { createOrUpdateResourcesDetails } from 'src/graphql/mutations';
import { listResourcesDetails } from '../../graphql/queries';
import { ResourceDetails, ResourceDetailsMutation } from '../../types/resources/resources.types';
import { formatApiError } from '../../utils/errorUtils';
import { logger } from '../../utils/logger';
import { fetchGraphQLRequest } from './clients/graphqlClient';

/**
 * Fetches resources from the GraphQL API
 * @returns Promise resolving to array of resource details
 * @throws Error if API call fails
 */
export const fetchResourcesAPI = async (): Promise<ResourceDetails[]> => {
  try {
    logger.info('Fetching resources from GraphQL API');
    const result = await fetchGraphQLRequest<{ listResourcesDetails: ResourceDetails[] }>({
      query: listResourcesDetails,
      variables: {},
      requestName: 'listResourcesDetails',
      requestType: 'query',
    });

    const resources = result.listResourcesDetails || [];
    logger.info(`Fetched ${resources.length} resources from API`);
    return resources;
  } catch (error) {
    logger.error('Error fetching resources from GraphQL API:', { error });
    throw formatApiError('Failed to fetch resources', error);
  }
};

/**
 * Updates resources from the GraphQL API
 * @param mutationData
 * @returns Promise<{ success: boolean; message: string }>
 * @throws Error if api call fails
 */
export const updateResourcesAPI = async (
  mutationData: ResourceDetailsMutation[]
): Promise<{ success: boolean; message: string }> => {
  try {
    logger.info(`Updating resources to GraphQL API:`, mutationData);
    const result = await fetchGraphQLRequest<{ createOrUpdateResourcesDetails: { numberOfRecordsUpdated: number } }>({
      query: createOrUpdateResourcesDetails,
      variables: { input: mutationData },
      requestName: 'createOrUpdateResourcesDetails',
      requestType: 'mutation',
      auditConfig: { pageName: 'resources' },
    });

    const numberOfRecordsUpdated = result.createOrUpdateResourcesDetails.numberOfRecordsUpdated || 0;
    logger.info(`Resources updated successfully`, { numberOfRecordsUpdated });
    return {
      success: numberOfRecordsUpdated > 0,
      message: `Resources updated successfully (${numberOfRecordsUpdated} records updated)`,
    };
  } catch (error) {
    logger.error('Error updating resources from GraphQL API:', { error });
    throw formatApiError('Failed to update resources', error);
  }
};
